import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Alert, AlertDescription } from './ui/alert'
import { 
  Search, 
  Filter, 
  Library, 
  FileText, 
  File,
  Image,
  Video,
  Archive,
  Loader2,
  ExternalLink,
  BookOpen,
  ChevronRight,
  X
} from 'lucide-react'
import { useAuth } from '../lib/auth-context'
import Navigation from './Navigation'

/**
 * AdvisorResources Component
 * 
 * Displays advisor-exclusive resources with the same UI as Resources
 * but filtered to show only advisor content (required_role = 'advisor')
 */
const AdvisorResources = () => {
  const [resources, setResources] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [downloadingId, setDownloadingId] = useState(null)
  const [expandedCard, setExpandedCard] = useState(null)
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    fetchResources()
    fetchCategories()
  }, [])

  /**
   * Toggle resource modal display state
   */
  const toggleCardExpansion = (resourceId) => {
    setExpandedCard(expandedCard === resourceId ? null : resourceId)
  }

  /**
   * Handle keyboard events and modal accessibility
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && expandedCard) {
        setExpandedCard(null)
      }
    }

    if (expandedCard) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [expandedCard])

  const fetchResources = async () => {
    try {
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/resources?section=advisor`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setResources(data.resources || [])
      } else {
        if (response.status === 403) {
          setError('Access denied. Advisor access required.')
        } else {
          setError('Failed to load resources')
        }
      }
    } catch (error) {
      console.error('Error fetching advisor resources:', error)
      setError('Failed to load resources')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Fetch available categories for filtering
   */
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/admin/categories?t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCategories(data.data?.categories || [])
      } else {
        console.warn('Failed to fetch categories, continuing without category filtering')
        setCategories([])
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setCategories([])
    }
  }

  const handleDownload = async (resourceId, fileName) => {
    try {
      setDownloadingId(resourceId)
      const token = localStorage.getItem('authToken')
      
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/api/resources/${resourceId}/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        window.open(data.download_url, '_blank')
      } else {
        setError('Failed to download resource')
      }
    } catch (error) {
      console.error('Error downloading resource:', error)
      setError('Failed to download resource')
    } finally {
      setDownloadingId(null)
    }
  }

  /**
   * Handle visiting link resources
   */
  const handleVisitLink = (resource) => {
    window.open(resource.file_url, '_blank')
  }

  /**
   * Handle resource card click based on content type
   */
  const handleResourceClick = (resource) => {
    if (resource.content_type === 'guide') {
      navigate(`/resources/${resource.slug}`);
    } else if (resource.content_type === 'link') {
      window.open(resource.file_url, '_blank');
    }
  }

  const getFileIcon = (fileType) => {
    if (!fileType) return File
    
    const type = fileType.toLowerCase()
    if (type.includes('pdf') || type.includes('document')) return FileText
    if (type.includes('image')) return Image
    if (type.includes('video')) return Video
    if (type.includes('zip') || type.includes('archive')) return Archive
    return File
  }

  const getContentTypeIcon = (contentType) => {
    switch(contentType) {
      case 'link': return <ExternalLink className="w-5 h-5" />;
      case 'guide': return <BookOpen className="w-5 h-5" />;
      default: return <ExternalLink className="w-5 h-5" />;
    }
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size'
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
  }

  /**
   * Parse markdown-like formatting in resource descriptions
   */
  const parseMarkdown = (text) => {
    if (!text) return ''
    
    let html = text
      .replace(/^#### (.+)$/gm, '<h4 class="text-lg font-semibold text-gray-900 mt-4 mb-2">$1</h4>')
      .replace(/^### (.+)$/gm, '<h3 class="text-xl font-semibold text-gray-900 mt-4 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-semibold text-gray-900 mt-4 mb-3">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="text-3xl font-bold text-gray-900 mt-4 mb-4">$1</h1>')
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^- (.+)$/gm, '<li>$1</li>')
      .replace(/(<li>.*<\/li>)/gs, '<ul class="list-disc list-inside my-2 space-y-1">$1</ul>')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">$1</a>')
      .replace(/\[([^\]]+)\]\(portal:\/\/([^)]+)\)/g, '<a href="/$2" class="text-blue-600 hover:text-blue-800 underline">$1</a>')
    
    return html
  }

  /**
   * Filter resources based on search term and selected category
   */
  const filteredResources = resources.filter(resource => {
    const matchesSearch = resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         resource.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesCategory = selectedCategory === 'all' || 
                           (resource.category_names && resource.category_names.includes(selectedCategory))
    
    return matchesSearch && matchesCategory
  })

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
            </div>
            <p className="text-gray-600 font-medium">Loading advisor resources...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Library className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Advisor Resources</h1>
              <p className="text-lg text-gray-600">Access exclusive guides, templates, tools and materials for advisors.</p>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6 border-red-200 bg-red-50 rounded-2xl">
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        {/* Search and Filter */}
        <div className="mb-8 space-y-4 md:space-y-0 md:flex md:items-center md:space-x-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-12 h-14 border-gray-300 focus:border-green-500 focus:ring-green-500 rounded-2xl bg-white/80 backdrop-blur-sm transition-all duration-200"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <Filter className="w-5 h-5 text-gray-500" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-14 px-4 border border-gray-300 rounded-2xl bg-white/80 backdrop-blur-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.name}>{category.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Resources Display */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Library className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No resources found</h3>
            <p className="text-gray-600">
              {searchTerm || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Resources will appear here once they are added'
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => {
              const isDownloading = downloadingId === resource.id
              
              return (
                <Card 
                  key={resource.id} 
                  className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white rounded-3xl shadow-lg cursor-pointer h-full flex flex-col"
                  onClick={() => {
                    if (resource.content_type === 'guide') {
                      handleResourceClick(resource);
                    } else {
                      toggleCardExpansion(resource.id);
                    }
                  }}
                >
                  <CardHeader className="pb-4 flex-shrink-0">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                          resource.content_type === 'guide' 
                            ? 'bg-purple-100' 
                            : 'bg-green-100'
                        }`}>
                          {resource.content_type === 'guide' ? (
                            <BookOpen className="w-6 h-6 text-purple-600" />
                          ) : (
                            <ExternalLink className="w-6 h-6 text-green-600" />
                          )}
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg font-semibold text-gray-900 line-clamp-2">
                            {resource.title}
                          </CardTitle>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  
                  <CardContent className="pt-0 flex-1 flex flex-col">
                    <CardDescription className="text-gray-600 mb-4 line-clamp-3 text-base leading-relaxed flex-1">
                      {resource.description}
                    </CardDescription>
                    
                    <div className="flex items-center justify-between mt-auto">
                      <div className="text-sm text-gray-500 font-medium">
                        Added {new Date(resource.created_at).toLocaleDateString()}
                      </div>
                      
                      {resource.content_type === 'guide' ? (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleResourceClick(resource)
                          }}
                          className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                          size="sm"
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          Read Guide
                        </Button>
                      ) : (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleResourceClick(resource)
                          }}
                          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                          size="sm"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Visit Link
                        </Button>
                      )}
                    </div>
                    
                    {resource.category_names && resource.category_names.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex flex-wrap gap-2">
                          {resource.category_names.map((categoryName, index) => (
                            <Badge 
                              key={index} 
                              variant="secondary" 
                              className="w-fit px-2.5 py-1 rounded-xl text-xs"
                              style={{ 
                                backgroundColor: resource.category_colors?.[index] ? 
                                  `${resource.category_colors[index]}20` : undefined,
                                color: resource.category_colors?.[index] || undefined
                              }}
                            >
                              {categoryName}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
        )}

        {/* Resource Modal Overlay */}
        {expandedCard && (() => {
          const selectedResource = filteredResources.find(r => r.id === expandedCard)
          if (!selectedResource) return null
          
          const FileIcon = getFileIcon(selectedResource.file_type)
          const isDownloading = downloadingId === selectedResource.id
          
          return (
            <div 
              className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
              onClick={() => setExpandedCard(null)}
            >
              <div 
                className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                      selectedResource.content_type === 'guide' 
                        ? 'bg-purple-100' 
                        : 'bg-green-100' 
                    }`}>
                      {selectedResource.content_type === 'guide' ? (
                        <BookOpen className="w-6 h-6 text-purple-600" />
                      ) : (
                        <ExternalLink className="w-6 h-6 text-green-600" />
                      )}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-1">
                        {selectedResource.title}
                      </h2>
                      {selectedResource.file_size && (
                        <p className="text-sm text-gray-500 font-medium">
                          {formatFileSize(selectedResource.file_size)}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedCard(null)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all duration-200"
                    title="Close resource"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-6 flex-1 overflow-y-auto">
                  {selectedResource.category_names && selectedResource.category_names.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-6">
                      {selectedResource.category_names.map((categoryName, index) => (
                        <Badge 
                          key={index} 
                          variant="secondary" 
                          className="w-fit px-2.5 py-1 rounded-xl text-xs"
                          style={{ 
                            backgroundColor: selectedResource.category_colors?.[index] ? 
                              `${selectedResource.category_colors[index]}20` : undefined,
                            color: selectedResource.category_colors?.[index] || undefined
                          }}
                        >
                          {categoryName}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="prose prose-sm max-w-none prose-headings:text-gray-900 prose-strong:text-gray-900 prose-ul:my-2 prose-li:my-1 mb-6">
                    <div 
                      className="text-gray-600 text-base leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: parseMarkdown(selectedResource.description || '') 
                      }} 
                    />
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-500 font-medium">
                      Added {selectedResource.created_at ? 
                        new Date(selectedResource.created_at).toLocaleDateString() : 'Recently'}
                    </div>
                    
                    {selectedResource.content_type === 'guide' ? (
                      <Button
                        onClick={() => handleResourceClick(selectedResource)}
                        className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                        size="lg"
                      >
                        <BookOpen className="w-5 h-5 mr-2" />
                        Read Guide
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleResourceClick(selectedResource)}
                        className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                        size="lg"
                      >
                        <ExternalLink className="w-5 h-5 mr-2" />
                        Visit Link
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })()}
      </div>
    </div>
  )
}

export default AdvisorResources

